import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalList, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage
      title="Terms and Conditions"
      effective="CREDIKING FINANCIAL SERVICE LIMITED · Effective Date: 22nd August, 2026"
    >
      <p className="text-foreground/90 font-medium">
        IMPORTANT: Please read these Terms and Conditions carefully before registering or using our
        services. By accessing or using the mobile application or website (the “Platform”), you agree
        to be bound by these Terms. If you do not agree, do not use the Platform.
      </p>

      <LegalSection title="1. Introduction and Acceptance">
        <p>
          1.1 These Terms constitute a legally binding agreement between you (“User”, “Borrower”) and
          CREDIKING FINANCIAL SERVICE LIMITED, a company duly incorporated under the laws of the
          Federal Republic of Nigeria.
        </p>
        <p>
          1.2 The Platform provides digital lending services, including loan applications,
          disbursements, repayments, and related financial services, subject to applicable Nigerian
          laws, including BOFIA 2020, CBN regulations, the FCCPA, the Nigeria Data Protection Act
          2023 (NDPA), and relevant FCCPC guidelines.
        </p>
        <p>
          1.3 We may update these Terms from time to time. We will notify you of material changes via
          the Platform, email, or SMS. Continued use after changes constitutes acceptance. You should
          review them periodically.
        </p>
      </LegalSection>

      <LegalSection title="2. Definitions">
        <LegalList
          items={[
            <>Loan: The principal amount disbursed plus applicable interest, fees, and charges.</>,
            <>NIN: National Identification Number.</>,
            <>BVN: Bank Verification Number.</>,
            <>Default: Failure to repay the Loan as agreed.</>,
            <>Platform: The mobile app and/or website operated by the Company.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Eligibility and Registration">
        <p>3.1 To use the Platform and apply for Loans, you must:</p>
        <LegalList
          items={[
            <>Be a Nigerian citizen or resident aged 18 years or older.</>,
            <>
              Provide valid identification, including NIN, BVN, a phone number linked to your details,
              and bank account information.
            </>,
            <>Have a valid email address and Nigerian bank account.</>,
            <>Not be under any legal disability or prohibited from entering contracts.</>,
          ]}
        />
        <p>
          3.2 You agree to provide accurate, complete, and up-to-date information. We reserve the
          right to verify information through third parties, credit bureaus, or government databases.
          False information may result in account suspension and loan rejection or immediate recall.
        </p>
        <p>
          3.3 You are responsible for maintaining the confidentiality of your login credentials.
          Notify us immediately of any unauthorized access.
        </p>
      </LegalSection>

      <LegalSection title="4. Loan Application, Approval, and Disbursement">
        <p>
          4.1 Loan applications are subject to our credit assessment, which may include analysis of
          your financial history, repayment capacity, and third-party data.
        </p>
        <p>
          4.2 Approval is at our sole discretion. We are not obligated to approve any application or
          provide reasons for rejection.
        </p>
        <p>
          4.3 Upon approval, loan terms (principal, interest rate, tenure, fees, repayment schedule)
          will be clearly displayed for your acceptance before disbursement. You must explicitly
          agree to the specific terms.
        </p>
        <p>
          4.4 Loans are disbursed electronically to your nominated bank account. Disbursement is not
          guaranteed on the same day.
        </p>
      </LegalSection>

      <LegalSection title="5. Loan Terms, Interest, and Fees">
        <p>
          5.1 Full details of interest rates, fees (processing, late payment, etc.), and total
          repayment amount will be disclosed upfront before you accept the Loan.
        </p>
        <p>
          5.2 Interest and charges are calculated as specified in the loan agreement. We comply with
          applicable CBN and FCCPC disclosure requirements. No hidden fees.
        </p>
        <p>
          5.3 You may prepay the Loan in full or in part. Early repayment may reduce interest; any
          fees will be as disclosed.
        </p>
      </LegalSection>

      <LegalSection title="6. Repayment">
        <p>
          6.1 Repayments are due as per the agreed schedule and may be made via the Platform, bank
          transfer, or authorized debit.
        </p>
        <p>
          6.2 You authorize us (and our partners) to debit your linked account or use other repayment
          mechanisms (e.g. Global Standing Instruction — GSI, where applicable) for due amounts.
        </p>
        <p>
          6.3 Failure to repay on time constitutes Default. Late fees and penalties will apply as
          disclosed.
        </p>
      </LegalSection>

      <LegalSection title="7. Default and Recovery">
        <p>7.1 In case of Default, we may:</p>
        <LegalList
          items={[
            <>Charge late fees and default interest as disclosed.</>,
            <>Report to credit bureaus, which may affect your future credit access.</>,
            <>
              Engage third-party debt recovery agents (who must comply with FCCPC rules prohibiting
              harassment, undue coercion, or abusive practices).
            </>,
            <>Institute legal proceedings to recover the debt, including costs and legal fees.</>,
            <>Suspend or terminate your access to the Platform.</>,
          ]}
        />
        <p>
          7.2 Debt recovery will be conducted lawfully and in line with consumer protection
          regulations. We do not tolerate prohibited practices such as unauthorized contact with
          third parties beyond permitted guarantors or references, public shaming, or threats.
        </p>
      </LegalSection>

      <LegalSection title="8. User Obligations and Representations">
        <p>8.1 You warrant that:</p>
        <LegalList
          items={[
            <>All information provided is true and accurate.</>,
            <>The Loan will be used for lawful purposes only.</>,
            <>You will not use the Platform for fraudulent or illegal activities.</>,
          ]}
        />
        <p>8.2 You agree not to share your account, circumvent security measures, or provide false repayment instructions.</p>
      </LegalSection>

      <LegalSection title="9. Privacy and Data Protection">
        <p>
          9.1 We collect, process, and store your personal data in accordance with the Nigeria Data
          Protection Act 2023 and other applicable laws. See our{" "}
          <Link to="/privacy" className="text-gold hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
        <p>
          9.2 By using the Platform, you consent to the collection, use, sharing (with credit
          bureaus, partners, regulators, and service providers), and processing of your data for
          credit assessment, loan administration, fraud prevention, marketing (where permitted), and
          regulatory compliance.
        </p>
        <p>
          9.3 You have rights under the NDPA, including access, rectification, erasure (subject to
          legal obligations), and objection. Contact our Data Protection Officer for requests.
        </p>
        <p>
          9.4 We implement appropriate technical and organizational security measures. In case of a
          data breach, we will notify you and the NDPC as required.
        </p>
        <p>9.5 We may use third-party processors. Data transfers outside Nigeria will comply with NDPA requirements.</p>
      </LegalSection>

      <LegalSection title="10. Intellectual Property">
        <p>
          The Platform, its content, logos, and software are owned by or licensed to the Company. You
          are granted a limited, non-exclusive, revocable license to use the Platform for personal
          purposes only. You may not copy, modify, reverse engineer, or distribute any part of the
          Platform.
        </p>
      </LegalSection>

      <LegalSection title="11. Prohibited Conduct">
        <p>
          You must not use the Platform in any way that violates laws, infringes rights, or interferes
          with its operation. This includes fraud, money laundering, or attempts to manipulate credit
          assessments.
        </p>
      </LegalSection>

      <LegalSection title="12. Termination and Suspension">
        <p>
          We may suspend or terminate your account at any time for breach of these Terms, Default, or
          regulatory reasons, with or without notice where permitted. You may close your account after
          repaying all outstanding obligations.
        </p>
      </LegalSection>

      <LegalSection title="13. Disclaimers and Limitation of Liability">
        <p>
          The Platform is provided “as is”. We do not guarantee uninterrupted access, error-free
          operation, or specific approval or loan amounts. To the maximum extent permitted by law, our
          total liability to you shall not exceed the amount of fees paid by you in the preceding 12
          months or the principal of the relevant Loan, whichever is lower. We are not liable for
          indirect, consequential, or punitive damages, or for delays due to force majeure.
        </p>
      </LegalSection>

      <LegalSection title="14. Indemnity">
        <p>
          You agree to indemnify and hold us harmless from any claims, losses, or damages arising from
          your breach of these Terms, misuse of the Platform, or violation of laws.
        </p>
      </LegalSection>

      <LegalSection title="15. Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by the laws of the Federal Republic of Nigeria. Disputes shall
          first be resolved amicably; failing that, by mediation or arbitration under the Arbitration
          and Mediation Act, or before competent Nigerian courts.
        </p>
      </LegalSection>

      <LegalSection title="16. Miscellaneous">
        <p>
          If any provision is invalid, the remainder remains in effect. Failure to enforce a right
          does not waive it. We may assign these Terms; you may not without our consent. These Terms
          (together with the specific Loan Agreement) constitute the entire agreement. Notices may be
          sent electronically, via SMS, or in-app.
        </p>
      </LegalSection>

      <LegalSection title="Contact Us">
        <p>CREDIKING FINANCIAL SERVICE LIMITED</p>
        <p>Address: No 6 University Road, Yaba, Lagos State</p>
        <p>Email: credikingltd@gmail.com</p>
        <p>Phone: 09122290559</p>
      </LegalSection>
    </LegalPage>
  );
}
